import toastMessages from "~/data/content/toast-messages.json";
import type { PD } from "~/types/pd";

export function usePd() {
   const $apiFetch = useNuxtApp().$apiFetch as typeof $fetch;
   const { log, logInfo, logError } = useDebugger();
   const { user, updateUserData } = useAuthStore();
   const { setProfilePd } = useProfile();
   const { addToast } = useToast();
   const { setPageTitle, setCrumbTitle } = useBreadcrumbs();

   const { track } = useMixpanel();
   const { $posthog } = useNuxtApp();

   // STATE ========================================
   const allPDs = useState<PD.SimpleCourse[] | null>("allPDs", () => null);
   const { onboardingCourses, queryOnboardingCourses } = useOnboardingCourses();
   const pdCourse = useState<PD.Course | null>("pdCourse", () => null);
   const pdSection = useState<PD.Section | null>("pdSection", () => null);
   const pdCardResponses = useState<PD.Response[]>(
      "pdCardResponses",
      () => [],
   );
   const pdCourseAllSectionCards = useState<PD.Card[]>(
      "pdCourseAllSectionCards",
      () => [],
   );
   const pdCourseDuration = useState<{ totalDuration: number } | null>(
      "pdCourseDuration",
      () => null,
   );
   const currentPdSectionProgress = ref<number>(0);
   const currentPdSectionDiscipline: Ref<"music" | "dance" | "drama"> = ref(
      disciplineText(user?.discipline ?? 0),
   );

   // QUERIES ========================================
   const queryPDs = async (): Promise<PD.SimpleCourse[]> => {
      try {
         const data = await $apiFetch<PD.IndexResponse>(`pd`, {
            params: { limit: 30, order_by: "sort" },
         });
         logInfo(`Latest PDs`, data);
         allPDs.value = data.data;
         return data.data;
      } catch (err) {
         logError("queryPDs failed", err);
         throw err;
      }
   };

   const queryPDsBySlug = async (
      slug: string,
   ): Promise<PD.Course | undefined> => {
      if (!slug) return;
      try {
         const data = await $apiFetch<PD.Course>(`pd/course/${slug}`, {});
         logInfo(`PD by Slug -- ${slug}`, data);
         pdCourse.value = data;
         setPageTitle(data.title);
         setCrumbTitle("pdCourse", data.title);
         return data;
      } catch (err) {
         logError(`queryPDsBySlug failed (${slug})`, err);
         throw err;
      }
   };

   const queryPDSection = async (slug: string): Promise<PD.Section> => {
      try {
         const data = await $apiFetch<PD.Section>(
            `pd/course/section/${slug}`,
            {},
         );
         logInfo(`PD Section by Slug ${slug}`, data);
         pdSection.value = data;
         setPageTitle(data.title);
         // Real title for the parent PD course crumb (avoids startCase(slug)).
         if (data.pd_course) setCrumbTitle("pdCourse", data.pd_course.title);
         // Endpoint quirk: progress is the loaded Progress relationship object
         // here; on pd/course/{slug} it's a flat number. Phase 0A target.
         // hasOne, so it's null until the user has any progress on the section.
         currentPdSectionProgress.value =
            typeof data.progress === "number"
               ? data.progress
               : data.progress?.percentage_complete ?? 0;
         return data;
      } catch (err) {
         logError(`queryPDSection failed (${slug})`, err);
         throw err;
      }
   };

   // Best-effort silent persistence: card responses are saved as the user
   // interacts. Logs and swallows so a transient failure doesn't disrupt the
   // session — the user can re-attempt by interacting with the card again.
   const handleCardResponse = async (
      method: "POST" | "PATCH",
      response: Partial<PD.Response> & { answer?: string | null },
   ): Promise<void> => {
      try {
         const data = await $apiFetch<PD.UpdateResponseResponse>(
            `pd/card/response`,
            {
               method,
               body: { ...response },
            },
         );
         log(
            `PD Response ${method == "POST" ? "Added" : "Updated"}`,
            response,
            data,
         );
         // PATCH with null answer returns { deleted: true } — skip cache update.
         if ("deleted" in data) return;
         const resIndex = pdCardResponses.value.findIndex(
            (res) => res.card_id === data.card_id,
         );
         if (resIndex > -1) {
            pdCardResponses.value[resIndex] = { ...data };
         } else {
            pdCardResponses.value.push({ ...data });
         }
      } catch (err) {
         logError("handleCardResponse failed", err);
      }
   };

   const queryCourseAllSectionCards = async (
      slug: string,
   ): Promise<{ totalDuration: number } | undefined> => {
      try {
         const courseData = await $apiFetch<PD.Course>(`pd/course/${slug}`, {});
         log(`queryCourseAllSectionCards ${slug}`, courseData);
         pdCourseAllSectionCards.value = [];
         let totalDuration = 0;

         for (const section of courseData.sections ?? []) {
            const sectionData = await $apiFetch<PD.Section>(
               `pd/course/section/${section.slug}`,
               {},
            );

            pdCourseAllSectionCards.value = [
               ...pdCourseAllSectionCards.value,
               ...(sectionData.cards ?? []).filter(
                  (card) => card.in_takeaway,
               ),
            ];

            totalDuration += Number(sectionData.duration);
         }
         pdCourseDuration.value = { totalDuration };
         return pdCourseDuration.value;
      } catch (error) {
         logError("Error fetching course or section data", error);
      }
   };

   // Self-contained UX: shows its own success/error toasts and does not re-throw.
   const completeUserPdOnboarding = async (): Promise<void> => {
      try {
         const data = await $apiFetch<ApiSuccess>(
            `/users/complete-onboarding-pd`,
            { method: "PATCH" },
         );
         updateUserData({ is_pd_course_completed: true });
         nextTick(() => {
            addToast(toastMessages.completePdOnboarding.success as Toast);
            log(`PD Section Progress Updated`, data);
         });
      } catch (err) {
         logError("completeUserPdOnboarding failed", err);
         addToast(toastMessages.completePdOnboarding.error as Toast);
      }
   };

   // Best-effort progress update — failures are logged but don't surface UX,
   // since this fires frequently as the user advances through a section.
   const updateSectionProgress = async (
      sectionSlug: string,
      progress: { progress: number },
   ): Promise<void> => {
      try {
         const data = await $apiFetch<PD.ProgressResponse>(
            `/pd/section/${sectionSlug}/progress`,
            {
               method: "POST",
               body: { ...progress },
            },
         );
         // update composable progress
         currentPdSectionProgress.value = data.progress;
         track("$pd_section_progress", {
            $PDCourse: pdCourse.value?.title,
            $PDSection: pdSection.value?.title,
            $sectionProgress: progress?.progress ?? 0,
         });
         $posthog().capture("pd_section_progress", {
            pd_course: pdCourse.value?.title,
            pd_section: pdSection.value?.title,
            section_progress: progress?.progress ?? 0,
         });
         // Track progress update in Mixpanel
         const sections = pdCourse.value?.sections ?? [];
         let courseProgress = 0;
         sections.forEach((section) => {
            const p =
               typeof section.progress === "number"
                  ? section.progress
                  : section.progress?.percentage_complete ?? 0;
            courseProgress += p;
         });
         courseProgress = sections.length
            ? Math.round(courseProgress / sections.length)
            : 0;

         track("$pd_course_progress", {
            $PDCourse: pdCourse.value?.title,
            $courseProgress: courseProgress,
         });

         log(`PD Section Progress Updated`, data);
      } catch (err) {
         logError("updateSectionProgress failed", err);
      }
   };

   // Self-contained UX: shows its own success/error toasts and does not re-throw.
   const savePdToProfile = async (
      slug: string,
      method: "POST" | "DELETE" = "POST",
      title: string,
   ): Promise<void> => {
      try {
         const data = await $apiFetch<PD.SaveResponse>(`pd/${slug}/save`, {
            method,
         });
         log(`Save PD To Profile ${method}: `, data);
         const toastMessage = (method === "POST"
               ? { ...toastMessages.addToProfile.success }
               : { ...toastMessages.removeFromProfile.success }) as Toast;
         toastMessage.message = toastMessage.message.replace(/%s/, title);
         addToast(toastMessage);
         setProfilePd(data);
      } catch (err) {
         logError("savePdToProfile failed", err);
         const toastMessage = (method === "POST"
               ? { ...toastMessages.addToProfile.error }
               : { ...toastMessages.removeFromProfile.error }) as Toast;
         toastMessage.message = toastMessage.message.replace(/%s/, title);
         addToast(toastMessage);
      }
   };

   // HELPERS ========================================
   const toggleBookmark = (card: PD.Card, response: PD.Response | undefined) => {
      if (response) {
         const pdResponseExists = pdCardResponses.value.find(
            (res) => res.card_id === card.id && res.type === "bookmark",
         );
         const cardDetails =
            pdResponseExists ||
            card.responses.find((res) => res.type === "bookmark");
         handleCardResponse("PATCH", {
            ...cardDetails,
            answer: response.answer,
         });
      } else {
         handleCardResponse("POST", {
            answer: '{"bookmark":"true"}',
            card_id: card.id,
            type: "bookmark",
         });
      }
   };

   const upsertCardResponse = (
      card: PD.Card,
      type: string,
      answer: string,
   ) => {
      const existing =
         pdCardResponses.value.find(
            (res) => res.card_id === card.id && res.type === type,
         ) ?? card.responses.find((res) => res.type === type);
      if (existing) {
         handleCardResponse("PATCH", { ...existing, answer });
      } else {
         handleCardResponse("POST", { answer, card_id: card.id, type });
      }
   };

   const saveNote = (card: PD.Card, response: unknown) =>
      upsertCardResponse(card, "note", JSON.stringify(response));

   const saveAnswer = (card: PD.Card, response: string) =>
      upsertCardResponse(card, "answer", response);

   return {
      // STATE
      allPDs,
      pdCourse,
      pdSection,
      pdCardResponses,
      pdCourseAllSectionCards,
      pdCourseDuration,
      currentPdSectionProgress,
      currentPdSectionDiscipline,
      onboardingCourses,
      // QUERIES
      queryPDs,
      queryPDsBySlug,
      queryPDSection,
      queryCourseAllSectionCards,
      queryOnboardingCourses,
      updateSectionProgress,
      savePdToProfile,
      completeUserPdOnboarding,
      // HELPERS
      toggleBookmark,
      saveNote,
      saveAnswer,
   };
}
